import DatePicker, {
  type ReactDatePickerCustomHeaderProps,
  type ReactDatePickerProps,
} from 'react-datepicker';
import {
  forwardRef,
  type SyntheticEvent,
  useState,
  type ReactNode,
  SelectHTMLAttributes,
} from 'react';
import dayjs from 'dayjs';

import { ChevronBackIc, ChevronForwardIc } from '@dsch/dd-icons';

import { Dropdown } from '../../../Toolkit/Dropdown/Dropdown';

import {
  Container,
  DatepickerContainer,
  Header,
  Select,
  SelectContainer,
  SelectGroup,
  SelectIcon,
  SelectedValue,
  SubtextContainer,
} from './Datepicker.styled';
import { Label } from '../../../Toolkit/Inputs/Label';
import { ErrorMessage } from '../../../Toolkit/Inputs/ErrorMessage';
import { HelpText } from '../../../Toolkit/Inputs/HelpText';

import { Spacing } from '../../../../helpers/genericStyles';
import { theme } from '../../../../helpers/Theme';
import { months } from './Datepicker.constants';

type DatepickerProps = {
  helpText?: string;
  errorMessage?: string;
  includeSubtext?: boolean;
  labelContent?: ReactNode;
  yearStart: number;
  yearEnd: number;
  placeholder?: string;
  hasError?: boolean;
  customHeaderSelectAriaLabel: string;
} & Spacing &
  ReactDatePickerProps;

type HeaderProps = {
  name: string;
  years: Array<number>;
  selectAriaLabel: string;
} & ReactDatePickerCustomHeaderProps;

type CustomSelectProps = {
  options: Array<string | number>;
} & SelectHTMLAttributes<HTMLSelectElement>;

const getRange = (start: number, end: number) => {
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

const CustomSelect = (props: CustomSelectProps) => {
  const { options, value, ...rest } = props;

  return (
    <SelectContainer>
      {/*
       ** Used to adjust the width of the select element to match the width of the selected value
       */}
      <SelectedValue aria-hidden={true}>{value}</SelectedValue>
      <Select value={value} {...rest}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
      <SelectIcon height={12} width={12} />
    </SelectContainer>
  );
};
const CustomHeader = (props: HeaderProps) => {
  const {
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    years,
    date,
    name,
    selectAriaLabel,
  } = props;

  return (
    <Header>
      <button onClick={decreaseMonth} type="button">
        <ChevronBackIc height={16} width={16} color={theme.colors.BLUE} />
      </button>
      <SelectGroup>
        <CustomSelect
          value={months[date.getMonth()]}
          onChange={({ target: { value } }) =>
            changeMonth(months.indexOf(value))
          }
          options={months}
          aria-label={`${selectAriaLabel} month`}
        />
        <CustomSelect
          value={date.getFullYear()}
          onChange={({ target: { value } }) => changeYear(parseInt(value))}
          options={years}
          data-testid={`${name}-year-dropdown`}
          aria-label={`${selectAriaLabel} year`}
        />
      </SelectGroup>
      <button onClick={increaseMonth} type="button">
        <ChevronForwardIc height={16} width={16} color={theme.colors.BLUE} />
      </button>
    </Header>
  );
};

const Datepicker = forwardRef<HTMLInputElement, DatepickerProps>(
  (props, ref) => {
    const {
      placeholder,
      helpText,
      onChange,
      includeSubtext = true,
      labelContent,
      mb,
      selected,
      calendarStartDay = 1,
      yearStart,
      yearEnd,
      errorMessage,
      name = 'unknown',
      value,
      hasError,
      customHeaderSelectAriaLabel,
      ...rest
    } = props;

    const [isOpen, setIsOpen] = useState(false);

    const handleChange = (
      date: Date | null,
      event: SyntheticEvent<HTMLInputElement, Event> | undefined,
    ) => {
      event?.preventDefault();
      onChange(date, event);
      setIsOpen(false);
    };

    const years = getRange(yearStart, yearEnd).reverse();

    const formatDate = (date: Date) => {
      return dayjs(date).format('DD/MM/YYYY');
    };

    return (
      <Container mb={mb}>
        <input ref={ref} type="hidden" value={value} />
        {labelContent && <Label htmlFor={name}>{labelContent}</Label>}
        <Dropdown
          placeholder={placeholder ?? ''}
          expanded={isOpen}
          setExpanded={setIsOpen}
          maxWidth="250px"
          displayedValue={selected ? formatDate(selected) : ''}
          hasError={hasError}
        >
          <DatepickerContainer>
            <DatePicker
              onChange={handleChange}
              inline
              selected={selected}
              calendarStartDay={calendarStartDay}
              renderCustomHeader={(props) => (
                <CustomHeader
                  {...props}
                  years={years}
                  name={name}
                  selectAriaLabel={customHeaderSelectAriaLabel}
                />
              )}
              name={name}
              {...rest}
            />
          </DatepickerContainer>
        </Dropdown>
        {includeSubtext && (
          <SubtextContainer>
            {errorMessage ? (
              <ErrorMessage text={errorMessage} />
            ) : (
              helpText && <HelpText text={helpText} />
            )}
          </SubtextContainer>
        )}
      </Container>
    );
  },
);

Datepicker.displayName = 'Datepicker';
export { Datepicker, type DatepickerProps };
